import { useEffect } from 'react';

// Formatted text shared by the site and the admin: older plain text
// (each line → a paragraph), plain-text versions (cards, meta tags), the
// brand palette with contrast checks, and the per-page "text style".

export const looksHtml = (s) => /<\/?[a-z][^>]*>/i.test(String(s || ''));
const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
export const plainToHtml = (s) => String(s || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => `<p>${esc(l)}</p>`).join('');
// HTML to show (the server sanitizes everything saved; plain text is escaped here).
export const toHtml = (s) => (looksHtml(s) ? String(s) : plainToHtml(s));
// Plain text of a formatted value (cards, meta description, "is it empty").
export function textOf(s) {
  if (!looksHtml(s)) return String(s || '').trim();
  return String(s).replace(/<(br|hr)\s*\/?>/gi, ' ').replace(/<\/(p|h2|h3|li|blockquote)>/gi, ' ').replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ').trim();
}

// Brand palette. On the site's light background gold and white text are
// not readable, so the site shows them in a readable form (dark gold; white
// on a navy chip) — see app.css .tc-*.
export const PALETTE = { navy: '#1c2a44', wood: '#5a3e2b', gold: '#c8a46a', red: '#b3202a', grey: '#4b5563', white: '#ffffff' };
const lin = (x) => { const c = x / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
const lum = (hex) => { const n = parseInt(hex.slice(1), 16); return 0.2126 * lin(n >> 16) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255); };
export const contrast = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p); return (x + 0.05) / (y + 0.05); };
export const PAGE_BG = '#f8fafc';
// Text colours allowed for headings (≥ 3:1, large text) and body / links (≥ 4.5:1).
export const readable = (key, large = false) => contrast(PALETTE[key], PAGE_BG) >= (large ? 3 : 4.5);

// ── Text style per page ─────────────────────────────────────────────────
export const FONTS = {
  site: { css: 'var(--font)' },
  playfair: { css: "'Playfair Display', Georgia, serif", gf: 'Playfair+Display:wght@600;700' },
  cormorant: { css: "'Cormorant Garamond', Georgia, serif", gf: 'Cormorant+Garamond:wght@500;600;700' },
  montserrat: { css: "'Montserrat', 'Inter', sans-serif", gf: 'Montserrat:wght@500;600;700' },
  inter: { css: "'Inter', system-ui, sans-serif" },
  lato: { css: "'Lato', 'Inter', sans-serif", gf: 'Lato:ital,wght@0,400;0,700;1,400' },
  sourceserif: { css: "'Source Serif 4', Georgia, serif", gf: 'Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400' },
  georgia: { css: "Georgia, 'Times New Roman', serif" },
};
export const TYPO_PRESETS = {
  elegant: { preset: 'elegant', headingFont: 'playfair', bodyFont: 'inter', bodySize: 17, lineHeight: 1.8, spacing: 'normal', maxWidth: 680, headingColor: 'navy', bodyColor: 'navy', linkColor: 'red', dropCap: false, lead: true, align: 'left' },
  modern: { preset: 'modern', headingFont: 'montserrat', bodyFont: 'inter', bodySize: 16, lineHeight: 1.6, spacing: 'normal', maxWidth: 760, headingColor: 'navy', bodyColor: 'grey', linkColor: 'red', dropCap: false, lead: false, align: 'left' },
  classic: { preset: 'classic', headingFont: 'cormorant', bodyFont: 'georgia', bodySize: 18, lineHeight: 1.8, spacing: 'airy', maxWidth: 680, headingColor: 'wood', bodyColor: 'navy', linkColor: 'wood', dropCap: true, lead: false, align: 'left' },
};
const GAP = { tight: '0.5em', normal: '0.85em', airy: '1.3em' };

// Class + CSS variables for a page's text style (none = the usual site style).
export function typoProps(t) {
  if (!t) return { className: '', style: undefined };
  const className = ['typo-scope', t.dropCap ? 'typo-dropcap' : '', t.lead ? 'typo-lead' : ''].filter(Boolean).join(' ');
  const style = {
    '--t-head-font': (FONTS[t.headingFont] || FONTS.site).css,
    '--t-body-font': (FONTS[t.bodyFont] || FONTS.inter).css,
    '--t-size': `${t.bodySize || 16}px`,
    '--t-lh': String(t.lineHeight || 1.7),
    '--t-gap': GAP[t.spacing] || GAP.normal,
    '--t-width': `${t.maxWidth || 760}px`,
    '--t-head': PALETTE[t.headingColor] || PALETTE.navy,
    '--t-body': PALETTE[t.bodyColor] || PALETTE.navy,
    '--t-link': PALETTE[t.linkColor] || PALETTE.red,
    '--t-align': t.align || 'left',
  };
  return { className, style };
}

// Loads the Google fonts a text style needs (once per family).
export function useTypoFonts(...styles) {
  const key = styles.filter(Boolean).map((t) => `${t.headingFont}|${t.bodyFont}`).join(',');
  useEffect(() => {
    const families = new Set();
    for (const t of styles) if (t) for (const f of [t.headingFont, t.bodyFont]) if (FONTS[f]?.gf) families.add(FONTS[f].gf);
    for (const fam of families) {
      const id = `gf-${fam.split(':')[0]}`;
      if (document.getElementById(id) || [...document.querySelectorAll('link[href*="fonts.googleapis"]')].some((l) => l.href.includes(fam.split(':')[0]))) continue;
      const l = document.createElement('link');
      l.id = id; l.rel = 'stylesheet'; l.href = `https://fonts.googleapis.com/css2?family=${fam}&display=swap`;
      document.head.appendChild(l);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
